// © Microsoft Corporation. All rights reserved.

import { AccessibilityHelpers, avatarFallback, close2, ellipsis2, enums, exclamation, openEditProfilePage, openProfilePage, placeholderImages, send, addOrUpdateTelemetryObject, renderHybrid } from "@msnews/social-utils";
import { Comment, Content, User } from "@msnews/social-data-service";
import { CommentBoxTelemetryObjects, commentBoxTelemetryActionDispatchers, commentBoxTelemetryContracts } from "./CommentBox.telemetry";
import { ComponentConnector, FollowInfo, PopupConnector, UsersConnector } from "../../SocialComment.props";

import DisabledMessage from "../disabled-message/DisabledMessage";
import ProfileEducator from "../profile-educator/ProfileEducator";
import React from "react";
import { SocialCommentClassNameContract } from "../../SocialComment.classnames";
import { SocialCommentLocalizedStrings } from "../../SocialComment.localizedStrings";
import { SocialCommentState } from "../../SocialComment.state";
import { String } from "typescript-string-operations";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { connect } from "react-redux";
import { getLocalStorage } from "@msnews/core";

export interface CommentBoxProps {
    componentConnector: ComponentConnector,
    isFirstCommenter: boolean,
    commentBoxType: string,
    parentType: string,
    parentUser: User,
    me: User,
    t: SocialCommentLocalizedStrings;
    managedClasses?: SocialCommentClassNameContract;
    usersConnector: UsersConnector;
    popupConnector: PopupConnector;
    users: { [id: string]: FollowInfo;}; // dictionary definening
    submitCommentAndUpdateState: (value: string) => Promise<Comment>;
    id: string;
    market: string;
    content: Content;
    broadcastAsTyping?: (id: string, market: string, content: Content) => void;
    mainTelemetryObject: TelemetryObjectBase;
    theme: string;
    commentId?: string;
    onCommentBoxInitialized?: (commentBox: HTMLDivElement) => void
}

interface CommentBoxState {
    value: string,
    showActions: boolean,
    lastTypedTime?: Date,
    lastBroadcastTypingTime?: Date,
    newCommentVisibleStatus: string,
    newCommentErrorMessage: string,
    remainingCharsCount: number,
    disabledHasClicked: boolean,
    disabled: boolean,
    focused: boolean,
    commentBoxFocusRoot: string,
    profileEducatorVisible: boolean;
}

class CommentBox extends React.Component<CommentBoxProps, CommentBoxState> {
    _isMounted = false;
    commentBoxRef: HTMLDivElement;
    containerRef: React.RefObject<HTMLDivElement>;
    signInRef: HTMLAnchorElement;
    telemetryObjects: CommentBoxTelemetryObjects;

    private static maxCharsLimit = 2000;

    constructor(props) {
        super(props);
        this.state = {
            value: "",
            showActions: false,
            lastTypedTime: undefined,
            lastBroadcastTypingTime: undefined,
            newCommentVisibleStatus: enums.visibleStatusType.Normal,
            newCommentErrorMessage: null,
            remainingCharsCount: CommentBox.maxCharsLimit,
            disabledHasClicked: false,
            disabled: false,
            focused: false,
            commentBoxFocusRoot: "keyboard",
            profileEducatorVisible: this.shouldRenderProfileEducator()
        };
        this.commentBoxRef = null;
        this.containerRef = React.createRef();
        this.signInRef = null;
        this.handleChange = this.handleChange.bind(this);
    }

    handleChange(e) {
        if (!e || !(this.commentBoxRef && this.state.focused)) { return; }

        e.stopPropagation();

        const charsCount = this.commentBoxRef.innerText.length;
        this.setState({showActions: charsCount > 0, remainingCharsCount: CommentBox.maxCharsLimit - charsCount});

        if (this.state.remainingCharsCount <= 0) {
            e.preventDefault();
        }

        if (!this.state.lastTypedTime || new Date().getTime() - this.state.lastTypedTime.getTime() > 20000) { // if 20sec passed since last recorded time
            this.setState({lastTypedTime: new Date()});
        }

        if (!this.state.lastBroadcastTypingTime || new Date().getTime() - this.state.lastBroadcastTypingTime.getTime() > 3000) {
            this.setState({lastBroadcastTypingTime: new Date()});
            if (this.props.broadcastAsTyping && this.props.me && this.props.me.isSocialUser) {
                this.props.broadcastAsTyping(this.props.id, this.props.market, this.props.content);
            }
        }
    }

    clear(keepCursorInBox) {
        if (this.commentBoxRef) {
            this.commentBoxRef.innerText = "";
            keepCursorInBox && this.commentBoxRef.focus();
        }
        this.setState({showActions: false, remainingCharsCount: CommentBox.maxCharsLimit });
    }

    async submitComment(e) {
        if (!this.state.disabled) {
            commentBoxTelemetryActionDispatchers.genericClick(e.currentTarget, this.telemetryObjects && this.telemetryObjects.sendComment);

            e.stopPropagation();

            this.commentBoxRef.innerHTML = this.commentBoxRef.innerHTML.replace(/(<a.*a>)/g, "").trim();

            this.setState({disabled: true, newCommentErrorMessage: undefined});
            const comment = await this.props.submitCommentAndUpdateState(this.commentBoxRef.innerText);
            this.setState({disabled: undefined});

            if (comment && comment.errorCode !== undefined) {
                this.setState({ newCommentErrorMessage: this.getErrorMessageFromCodes(comment.errorCode, comment.errorSubCodes)});
            } else if (comment) {
                if (comment.visibleStatus == enums.visibleStatusType.Normal || comment.visibleStatus == enums.visibleStatusType.InReview) {
                    this.clear(false);
                }

                this.setState({ newCommentVisibleStatus: comment.visibleStatus});
            }
        }
    }

    placeCaretAtEnd(el) {
        el.focus();
        if (typeof window.getSelection != "undefined" && typeof document.createRange != "undefined") {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }

    componentDidUpdate() {
        AccessibilityHelpers.attachAccessibilityListeners(document);
    }

    listenKeyEvents(el) {
        if (!el || this._isMounted) {
            return;
        }
        this._isMounted = true;
        const capture = {
            capture: true
        };
        this.commentBoxRef = el;
        this.commentBoxRef.addEventListener("keyup", this.handleChange, capture);
        this.commentBoxRef.addEventListener("keyupCapture", this.handleChange, capture);
        this.commentBoxRef.addEventListener("keypress", this.handleChange, capture);
        this.commentBoxRef.addEventListener("keypressCapture", this.handleChange, capture);
        if (this.props.commentBoxType == "Reply") {
            this.placeCaretAtEnd(this.commentBoxRef);
        }
        if (this.props.onCommentBoxInitialized) { this.props.onCommentBoxInitialized(this.commentBoxRef); }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.commentBoxRef) {
            this.commentBoxRef.removeEventListener("keyup", this.handleChange);
            this.commentBoxRef.removeEventListener("keyupCapture", this.handleChange);
            this.commentBoxRef.removeEventListener("keypress", this.handleChange);
            this.commentBoxRef.removeEventListener("keypressCapture", this.handleChange);
        }
    }

    handleSignInClick(e) {

        commentBoxTelemetryActionDispatchers.genericClick(e.currentTarget, this.telemetryObjects && this.telemetryObjects.signIn);
        this.props.popupConnector.openPopup("multiLoginPage", { contentId: this.props.content.id, focusBackRef: this.signInRef});
    }

    commentBoxClickHandler(e, isDisabled) {
        const me = this.props.me;

        if (me.state == enums.userStateType.newUser) {
            this.props.popupConnector.openPopup("onboardingPage", { contentId: this.props.content.id });
        } else if (me.state == enums.userStateType.anonymous) {
            this.handleSignInClick(e);
        } else if (isDisabled) {
            this.setState({ disabledHasClicked: true });
        }
    }

    commentBoxPasteHandler(e) {
        let content;
        e.preventDefault();

        if (e.clipboardData) {
            content = e.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, content);
        } else if (window.clipboardData) {
            content = window.clipboardData.getData("Text");
            if (window.getSelection) { window.getSelection().getRangeAt(0).insertNode(document.createTextNode(content)); }
        }

        this.commentBoxRef.innerText = this.commentBoxRef.innerText.substring(0, CommentBox.maxCharsLimit + 1);
    }

    getErrorMessageFromCodes(code, subCodes): string {
        const maxSubCode = subCodes && subCodes.length > 0 ? Math.max(...subCodes) : null;

        if (enums.newCommentErrorCodeMap[code] && enums.newCommentErrorCodeMap[code][maxSubCode]) {
            return enums.newCommentErrorCodeMap[code][maxSubCode];
        } else if (enums.newCommentErrorCodeMap[code]) {
            return enums.newCommentErrorCodeMap[code];
        } else {
            return enums.newCommentErrorCodeMap[0];
        }
    }

    getPlaceholderText() {
        const { me, t, commentBoxType, isFirstCommenter, content } = this.props;

        if (content.isExpired) {
            return t.CommentBoxPlaceholderExpired;
        }

        if (me.state == enums.userStateType.anonymous) {
            return t.CommentBoxPlaceholderAnonymousUser;
        } else if (me.state == enums.userStateType.newUser) {
            return t.CommentBoxPlaceholderNewUser;
        } else if (me.isBanned) {
            return t.CommentBoxPlaceholderBannedUser;
        } else if (me.nameStatus == enums.userNameStatusType.notAllow) {
            return t.CommentBoxPlaceholderNameNotAllow;
        } else if (commentBoxType == "Comment") {
            if (isFirstCommenter) {
                return t.CommentBoxPlaceholderFirstComment;
            }
            return t.CommentBoxPlaceholderMsg;
        } else {
            return t.CommentBoxPlaceholderReply;
        }
    }

    getAvatarAltText() {
        const { me, t } = this.props;

        if (me.nameStatus == enums.userNameStatusType.notAllow) {
            return t.NameNotAllowTip.replace("{#}", me.name);
        } else if (me.avatarStatus == enums.userAvatarStatusType.notAllow) {
            return t.AvatarNotAllowTip;
        } else if (me.nameStatus == enums.userNameStatusType.inReview) {
            return t.NameInReviewTip;
        } else if (me.avatarStatus == enums.userAvatarStatusType.inReview) {
            return t.AvatarInReviewTip;
        } else if (me.isBanned) {
            return t.UserBannedTip;
        } else {
            return t.AvatarAltText;
        }
    }

    handleFocus(e) {
        this.setState({focused: true});

        if (this.state.commentBoxFocusRoot == "keyboard") {
            const el = e.target;
            const range = document.createRange();
            range.selectNodeContents(el);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }

    render() {
        const {componentConnector, commentBoxType, parentType, parentUser, me, t, managedClasses, market, content, mainTelemetryObject, theme, commentId, isFirstCommenter} = this.props;

        if (!me || !content) { return null; }

        const avatarUrl = me.state != enums.userStateType.newUser && me.avatarStatus == enums.userAvatarStatusType.normal ? me.avatarUrl || placeholderImages.avatar : placeholderImages.avatar;
        const placeholderText = this.getPlaceholderText();
        const meHasInReviewInfo = (me.avatarStatus == enums.userAvatarStatusType.inReview || me.nameStatus == enums.userNameStatusType.inReview);
        const meHasNotAllowInfo = (me.avatarStatus == enums.userAvatarStatusType.notAllow || me.nameStatus == enums.userNameStatusType.notAllow || (!meHasInReviewInfo && me.isBanned));
        const disabled = me.state != enums.userStateType.existingUser || content.isExpired || me.isBanned || this.state.disabled || me.nameStatus == enums.userNameStatusType.notAllow;
        const isDeleted = this.state.newCommentVisibleStatus != enums.visibleStatusType.Normal &&
                      [enums.visibleStatusType.DeletedByAI,
                          enums.visibleStatusType.DeletedByHI,
                          enums.visibleStatusType.DeletedByUser].includes(this.state.newCommentVisibleStatus);
        const isInReview = this.state.newCommentVisibleStatus == enums.visibleStatusType.InReview;
        const hasDeniedMessage = this.state.newCommentErrorMessage && (t[this.state.newCommentErrorMessage] || t[enums.newCommentErrorCodeMap[0]]);

        /** Telemetry */
        this.telemetryObjects = {
            postViolationAI: addOrUpdateTelemetryObject(mainTelemetryObject, commentBoxTelemetryContracts.postViolationAI, { id: commentId || "" }, this.telemetryObjects && this.telemetryObjects.postViolationAI),
            postHumanReview: addOrUpdateTelemetryObject(mainTelemetryObject, commentBoxTelemetryContracts.postHumanReview, { id: commentId || "" }, this.telemetryObjects && this.telemetryObjects.postHumanReview),
            sendComment: commentBoxType == "Comment"
                ? addOrUpdateTelemetryObject(mainTelemetryObject, commentBoxTelemetryContracts.sendComment, { id: content && content.cmsId }, this.telemetryObjects && this.telemetryObjects.sendComment)
                : addOrUpdateTelemetryObject(mainTelemetryObject, commentBoxTelemetryContracts.sendReply, { id: commentId || ""}, this.telemetryObjects && this.telemetryObjects.sendComment),
            signIn: addOrUpdateTelemetryObject(mainTelemetryObject, commentBoxTelemetryContracts.signIn, { id: content && content.cmsId }, this.telemetryObjects && this.telemetryObjects.signIn),
            userAvatarLink: addOrUpdateTelemetryObject(mainTelemetryObject, {...commentBoxTelemetryContracts.profileLink}, { id: me.traceId, headline: me.name}, this.telemetryObjects && this.telemetryObjects.userAvatarLink),
            bannedViewSelfProfileLink: addOrUpdateTelemetryObject(mainTelemetryObject, commentBoxTelemetryContracts.profileLink, { id: me.id }, this.telemetryObjects && this.telemetryObjects.bannedViewSelfProfileLink),
            editProfileLink: addOrUpdateTelemetryObject(mainTelemetryObject, commentBoxTelemetryContracts.editProfileLink, { id: me.id }, this.telemetryObjects && this.telemetryObjects.editProfileLink)
        };

        return (
            <div className={`${managedClasses.commentForm} cwt-theme-2`} ref={this.containerRef}>
                <div className="cwt-comment-box-container">
                    {this.state.profileEducatorVisible && commentBoxType == "Comment" &&
                        <ProfileEducator
                            {...{ managedClasses, t }}
                            onHide={() => this.setState({profileEducatorVisible: false})}
                        />
                    }
                    <div className="cwt-avatar-container" >
                        <a
                            {...openProfilePage(componentConnector, theme, me.id, market, "_blank")}
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.userAvatarLink, false)}
                        >
                            <img
                                alt={t.AriaLabelsProfilePicture}
                                className="cwt-commentbox-avatar"
                                src={avatarUrl}
                                onError={(e)=>{ avatarFallback(e); }}
                            />
                        </a>
                        { meHasInReviewInfo &&
                            <i className="cwt-icon cwt-status-mark cwt-inreview"> {ellipsis2} </i>
                        }
                        {(meHasNotAllowInfo || content.isExpired) &&
                            <i className="cwt-icon cwt-status-mark cwt-notallow"> {exclamation} </i>
                        }
                    </div>
                    <div
                        className={`form-control cwt-comment-box ${this.state.disabledHasClicked && "cwt-warning"}`}
                        contentEditable={!disabled}
                        ref={el => this.listenKeyEvents(el)}
                        placeholder = { placeholderText}
                        suppressContentEditableWarning={true}
                        role="textbox"
                        aria-label={isFirstCommenter ? t.AriaLabelsCommentBoxFirstCommenter :  t.AriaLabelsCommentBox}
                        aria-multiline={!disabled}
                        aria-readonly={disabled}
                        onClick={ e => this.commentBoxClickHandler(e, disabled)}
                        onPaste={ e => this.commentBoxPasteHandler(e)}
                        onFocus={ e => { this.handleFocus(e); }}
                        onKeyDown={ e => { this.setState({commentBoxFocusRoot: "keyboard"}); }}
                        onMouseEnter={ e => { this.setState({commentBoxFocusRoot: "mouse"}); }}
                        onBlur={() => this.setState({focused: false})}
                    >
                        {commentBoxType == "Comment" || parentType == "Comment" ? "" :
                            <React.Fragment>
                                <a className="cwt-name cwt-link" contentEditable={false}>{parentUser.name}</a>
                                &nbsp;
                            </React.Fragment>
                        }
                    </div>

                    <div className="cwt-button-container">
                        { this.state.showActions &&
                            <React.Fragment>
                                <a className={`cwt-link cwt-clear-comment-btn ${disabled && "disabled"}`} role="button" aria-label={t.AriaLabelsDiscardComment} onClick={(e) => this.clear(true)} >
                                    <i className="cwt-icon">{close2}</i>
                                </a>
                                <a className={`cwt-link cwt-submit-comment-btn ${disabled && "disabled"}`} role="button" aria-label={t.AriaLabelsSendComment} onClick={(e) => this.submitComment(e)} {...renderHybrid(this.telemetryObjects.sendComment, false)}>
                                    <i className="cwt-icon">{send}</i>
                                </a>
                            </React.Fragment>
                        }
                        { me.state == enums.userStateType.anonymous &&
                            <a className={"cwt-link"}  aria-haspopup="true" role="button" onClick={ (e) => this.handleSignInClick(e)} {...renderHybrid(this.telemetryObjects.signIn, false, (el) => { this.signInRef = el; })}>
                                {t.CommentBoxButtonSignIn}
                            </a>
                        }
                        { (me.nameStatus == enums.userNameStatusType.notAllow || me.avatarStatus == enums.userAvatarStatusType.notAllow) &&
                            <a
                                className={"cwt-link"}
                                {...openEditProfilePage(theme, market)}
                                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.editProfileLink, false)}
                            >
                                {me.nameStatus == enums.userNameStatusType.notAllow ? t.CommentBoxButtonChangeName : t.CommentBoxButtonEditProfile}
                            </a>
                        }
                        { me.isBanned &&
                            <a
                                className={"cwt-link"}
                                {...openProfilePage(componentConnector, theme, me.id, market, "_blank")}
                                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.bannedViewSelfProfileLink, false)}
                            >
                                {t.CommentBoxButtonViewProfile}
                            </a>
                        }
                    </div>
                </div>

                {this.state.remainingCharsCount < 100 && <div className="cwt-character-remaining-msg">{this.state.remainingCharsCount > 0 ? String.Format(t.CommentBoxCharsRemaining, {remainingCharsCount: this.state.remainingCharsCount}) : t.CommentBoxCharsLimitReached }</div>}

                { isDeleted && <DisabledMessage {...{ managedClasses, showGuidelines: true, message: t.NewPostDeniedMsg, t, componentConnector, theme, market, mainTelemetryObject: this.telemetryObjects.postViolationAI }}/> }

                { isInReview && <DisabledMessage {...{ managedClasses, showGuidelines: true, message: t.NewPostInReviewMsg, t, componentConnector, theme, market, mainTelemetryObject: this.telemetryObjects.postHumanReview }}/> }

                { hasDeniedMessage && <DisabledMessage {...{ managedClasses, showGuidelines: true, message: hasDeniedMessage, t, componentConnector, theme, market, mainTelemetryObject: this.telemetryObjects.postViolationAI }}/> }

            </div>
        );
    }

    shouldRenderProfileEducator() {
        const { me } = this.props;
        return me && me.isSocialUser && !getLocalStorage().getItem(enums.profileVisitedLocalStorageKey);
    }
}
const mapStateToProps = (state: SocialCommentState) => {
    return { id: state.id };
};

export default connect(mapStateToProps)(CommentBox);
export { CommentBox as CommentBoxBase };

